import type { PropsWithChildren } from 'react'
import {
  Room,
  RoomEvent,
  ConnectionState,
  ParticipantEvent,
} from 'livekit-client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export type LiveKitConnectionParams = {
  url: string
  token: string
  autoSubscribe?: boolean
  spaceId?: string
  disableAudioAutoplay?: boolean
}

export type LiveKitConnectResult = {
  room: Room
  wasAlreadyConnected: boolean
}

type LiveKitContextValue = {
  room: Room | null
  isConnecting: boolean
  connect: (params: LiveKitConnectionParams) => Promise<LiveKitConnectResult>
  disconnect: () => Promise<void>
}

type RoomWithAudioInternals = {
  acquireAudioContext?: () => Promise<void> | void
}

const LiveKitContext = createContext<LiveKitContextValue | undefined>(undefined)

export function LiveKitProvider({ children }: PropsWithChildren) {
  const [room, setRoom] = useState<Room | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const roomRef = useRef<Room | null>(null)
  const currentSpaceIdRef = useRef<string | null>(null)
  const activeConnectRef = useRef<{
    key: string
    promise: Promise<LiveKitConnectResult>
  } | null>(null)

  const cleanupRoom = useCallback((targetRoom?: Room | null) => {
    const roomToCleanup = targetRoom || roomRef.current
    if (!roomToCleanup) {
      return
    }

    roomToCleanup.removeAllListeners()
    roomToCleanup.disconnect(true)

    if (roomRef.current === roomToCleanup) {
      roomRef.current = null
      currentSpaceIdRef.current = null
      setRoom(null)
    }
  }, [])

  const connect = useCallback(
    async ({
      url,
      token,
      autoSubscribe = true,
      spaceId,
      disableAudioAutoplay = false,
    }: LiveKitConnectionParams): Promise<LiveKitConnectResult> => {
      if (!url || !token) {
        throw new Error('Missing LiveKit connection parameters.')
      }

      const connectionKey = `${spaceId || 'default'}:${url}:${token}`
      const activeConnect = activeConnectRef.current
      if (activeConnect) {
        if (activeConnect.key === connectionKey) {
          return activeConnect.promise
        }

        try {
          await activeConnect.promise
        } catch {
          // Ignore stale connection errors before starting a new attempt.
        }
      }

      const currentRoom = roomRef.current
      if (currentRoom && currentRoom.state === ConnectionState.Connected) {
        if (
          spaceId &&
          currentSpaceIdRef.current &&
          spaceId !== currentSpaceIdRef.current
        ) {
          cleanupRoom()
        } else {
          return { room: currentRoom, wasAlreadyConnected: true }
        }
      }

      const connectPromise = (async () => {
        setIsConnecting(true)
        cleanupRoom()

        const nextRoom = new Room({
          adaptiveStream: true,
          dynacast: true,
          webAudioMix: false,
          reconnectPolicy: {
            nextRetryDelayInMs: () => 2000,
          },
        })

        nextRoom
          .on(RoomEvent.Disconnected, () => {
            cleanupRoom(nextRoom)
          })
          .on(RoomEvent.Reconnecting, () => {
            if (roomRef.current === nextRoom) {
              setIsConnecting(true)
            }
          })
          .on(RoomEvent.Reconnected, () => {
            if (roomRef.current === nextRoom) {
              setIsConnecting(false)
            }
          })

        if (disableAudioAutoplay) {
          const textOnlyRoom = nextRoom as unknown as RoomWithAudioInternals
          textOnlyRoom.acquireAudioContext = async () => undefined
          nextRoom.localParticipant.off(
            ParticipantEvent.AudioStreamAcquired,
            nextRoom.startAudio,
          )
        }

        const maxRetries = 3
        let lastError: Error | null = null

        for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
          try {
            console.info('LiveKit connect attempt', {
              attempt,
              maxRetries,
              url,
              spaceId,
            })
            await nextRoom.connect(url, token, { autoSubscribe })
            console.info('LiveKit connected', {
              roomName: nextRoom.name,
              spaceId,
              participantIdentity: nextRoom.localParticipant.identity,
            })
            roomRef.current = nextRoom
            currentSpaceIdRef.current = spaceId ?? null
            setRoom(nextRoom)
            setIsConnecting(false)
            return { room: nextRoom, wasAlreadyConnected: false }
          } catch (error) {
            lastError =
              error instanceof Error ? error : new Error('LiveKit connection failed')
            console.warn('LiveKit connect failure', {
              attempt,
              maxRetries,
              message: lastError.message,
              spaceId,
            })

            if (attempt < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
              continue
            }
          }
        }

        cleanupRoom(nextRoom)
        throw lastError || new Error('Unable to connect to LiveKit')
      })()

      activeConnectRef.current = {
        key: connectionKey,
        promise: connectPromise,
      }

      try {
        return await connectPromise
      } finally {
        if (activeConnectRef.current?.promise === connectPromise) {
          activeConnectRef.current = null
        }
        setIsConnecting(false)
      }
    },
    [cleanupRoom],
  )

  const disconnect = useCallback(async () => {
    activeConnectRef.current = null
    cleanupRoom()
  }, [cleanupRoom])

  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupRoom()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      cleanupRoom()
    }
  }, [cleanupRoom])

  const value = useMemo(
    () => ({
      room,
      isConnecting,
      connect,
      disconnect,
    }),
    [connect, disconnect, isConnecting, room],
  )

  return (
    <LiveKitContext.Provider value={value}>
      {children}
    </LiveKitContext.Provider>
  )
}

export function useLiveKit() {
  const context = useContext(LiveKitContext)

  if (!context) {
    throw new Error('useLiveKit must be used within a LiveKitProvider')
  }

  return context
}
